import { useState } from "react";
import { gaEvent } from "../lib/ga";
import { ArrowLeft, ArrowRight, RotateCcw, Rocket, CheckCircle2, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const QUESTIONS = [
  { topic: "Growth Strategy", q: "Can you clearly explain what growth milestone you are trying to reach in the next 12 to 24 months?",
    opts: ["Yes — I have a specific, written growth target", "Roughly — I have a general idea but it is not clearly defined", "Not yet — I have not defined this"],
    action: "Define your 12 to 24 month growth target in writing. Be specific — a number, a milestone, a date.", actionBelow: 2 },
  { topic: "Use of Funds", q: "Do you know exactly what you will spend the capital on and in what order of priority?",
    opts: ["Yes — I have a detailed use of funds breakdown", "Partially — I know the general areas but not the detail", "No — I have not worked this out yet"],
    action: "Build a use of funds breakdown. List every area you will spend capital on, in order of priority, with KES amounts.", actionBelow: 2 },
  { topic: "Capital to Growth Link", q: "Can you clearly say: if I raise X amount, my revenue will grow by Y because of Z?",
    opts: ["Yes — I can explain this clearly with numbers", "Roughly — I have a general sense but not backed by numbers", "No — I have not made this connection yet"],
    action: "Work out your capital-to-growth multiplier. If you raise X, what specific action does that fund, and what does that action produce in revenue?", actionBelow: 2 },
  { topic: "Revenue Status", q: "Is your business generating revenue today?",
    opts: ["Yes — we have consistent monthly revenue", "Early stage — we have some revenue but it is not yet consistent", "No — we are pre-revenue"],
    action: "Focus on getting to first revenue before approaching most funders. Pre-revenue businesses can raise but the bar is much higher and the terms will be less favourable.", actionBelow: 1 },
  { topic: "Financial Model", q: "Do you have a financial model that covers at least 3 years of forecasts?",
    opts: ["Yes — it is built, up to date, and formula-driven", "Partially — I have something but it needs work", "No — I do not have a financial model yet"],
    action: "Build or complete your financial model. It must cover 3 historical years and 5 forecast years, with all outputs formula-driven.", actionBelow: 2 },
  { topic: "Key Metrics", q: "Do you know your gross margin, monthly burn rate, and monthly revenue right now without looking them up?",
    opts: ["Yes — I know all three", "I know some but not all of them", "No — I am not tracking these yet"],
    action: "Start tracking your key metrics weekly. Gross margin, burn rate, and monthly revenue are the three numbers every funder will ask for in the first meeting.", actionBelow: 2 },
  { topic: "Unit Economics", q: "Do you know how much it costs you to acquire one customer, and what that customer is worth over their lifetime with you?",
    opts: ["Yes — I know both figures", "I have a rough estimate for one or both", "No — I have not calculated these yet"],
    action: "Calculate your CAC and LTV. Divide your total sales and marketing spend by new customers acquired to get CAC. Multiply average monthly revenue per customer by average customer lifetime to get LTV.", actionBelow: 2 },
  { topic: "Funder Documentation", q: "Do you have a pitch deck, financial model, management accounts, and bank statements ready to send to a funder today?",
    opts: ["Yes — all of the above are ready", "Some of it is ready but not all", "None of it is ready yet"],
    action: "Complete your funder documentation stack — pitch deck, financial model, 12 months of management accounts, and 6 months of bank statements. Do not approach funders until all four are ready.", actionBelow: 2 },
  { topic: "Type of Capital", q: "Do you know whether your business needs equity, debt, or revenue-based financing — and can you explain why?",
    opts: ["Yes — I know which type fits my business and why", "I have a general sense but I am not fully sure", "No — I have not thought through this yet"],
    action: "Research the types of capital available and match them to your business. Equity suits high-growth businesses willing to dilute. Debt suits cash-flow-positive businesses with assets. Revenue-based financing suits businesses with predictable monthly revenue.", actionBelow: 2 },
  { topic: "Funder Identification", q: "Do you have a shortlist of at least 3 funders who are a strong fit for your business stage, sector, and capital needs?",
    opts: ["Yes — I have a specific shortlist with contact details", "I know of some funders but have not built a proper shortlist", "No — I have not identified funders yet"],
    action: "Build your funder shortlist. Research funders active in your sector and stage in East Africa. Aim for a shortlist of at least 5 with ticket sizes that match your raise.", actionBelow: 2 },
  { topic: "Legal Readiness", q: "Is your business legally registered with a clean ownership structure and no unresolved legal or ownership disputes?",
    opts: ["Yes — everything is clean and documented", "Mostly — there are minor things to tidy up", "No — there are unresolved issues or we are not yet registered"],
    action: "Resolve any legal or ownership issues before approaching funders. Funders conduct legal due diligence and unresolved issues will either kill a deal or significantly delay it.", actionBelow: 2 },
  { topic: "Capital Deployment Plan", q: "If capital landed in your account tomorrow, do you have a clear 90-day deployment plan?",
    opts: ["Yes — I have a milestone-based deployment plan ready", "Roughly — I know the priorities but it is not a formal plan", "No — I have not thought this through yet"],
    action: "Write your 90-day capital deployment plan before your first funder meeting. Funders will ask how you plan to deploy the capital — a vague answer loses deals.", actionBelow: 2 },
];

// option index 0 = A (2 pts), 1 = B (1 pt), 2 = C (0 pts)
const POINTS = [2, 1, 0];
const LETTERS = ["A", "B", "C"];

const BANDS = {
  ready: {
    label: "Funder-Ready", color: "#22C55E", bg: "bg-green-500/15", border: "border-green-500/40", text: "text-green-400",
    summary: "Your business shows strong capital readiness. You have the foundations funders look for — clear growth strategy, financial documentation, and a defined use of funds. Focus now on identifying the right funders and initiating conversations.",
  },
  nearly: {
    label: "Nearly There", color: "#F59E0B", bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-400",
    summary: "You are close to being funder-ready. A few gaps are holding your score back. Fix the items below before approaching funders — they will ask about these in the first meeting.",
  },
  notready: {
    label: "Not Ready", color: "#EF4444", bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-400",
    summary: "Your business needs more preparation before approaching funders. This is not a bad thing — most businesses are not ready the first time they assess themselves. Work through the action plan below and reassess in 60 to 90 days.",
  },
};

export default function CapitalReadinessQuiz() {
  const [step, setStep] = useState(0); // 0..11 questions, 12 = email gate → results
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [leadDone, setLeadDone] = useState(false);

  const finished = step >= QUESTIONS.length;
  const atGate = finished && !leadDone;
  const done = finished && leadDone;
  const raw = answers.reduce((s, a) => s + (a === null ? 0 : POINTS[a]), 0);
  const score = Math.min(100, Math.round(raw * 4.17));
  const band = score >= 70 ? BANDS.ready : score >= 55 ? BANDS.nearly : BANDS.notready;
  const gaps = QUESTIONS.filter((qq, i) => answers[i] !== null && POINTS[answers[i]] < qq.actionBelow);

  const reset = () => {
    setAnswers(Array(QUESTIONS.length).fill(null));
    setStep(0);
    setLeadDone(false);
    setEmailError("");
  };

  const submitLead = async () => {
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setSubmitting(true);
    gaEvent("quiz_complete", { quiz: "capital_readiness", score, band: band.label });
    try {
      await fetch(`${API}/api/quiz-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: clean,
          score,
          band: band.label,
          answers: answers.map((a) => (a === null ? "" : LETTERS[a])),
        }),
      });
    } catch (e) {
      // never block the founder's results on a network hiccup
    }
    setSubmitting(false);
    setLeadDone(true);
  };

  return (
    <div className="min-h-screen bg-[#04302E] text-white"
      style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }}
      data-testid="capital-readiness-quiz">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-16">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">Clinq</p>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">
          Capital Readiness Self-Assessment
        </h1>

        {!finished && (
          <>
            <div className="mt-8" data-testid="crq-progress">
              <div className="flex items-center justify-between text-xs font-semibold text-white/60">
                <span data-testid="crq-progress-label">Question {step + 1} of {QUESTIONS.length}</span>
                <span>{Math.round((step / QUESTIONS.length) * 100)}%</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-[#F59E0B] transition-[width] duration-300"
                  style={{ width: `${(step / QUESTIONS.length) * 100}%` }} />
              </div>
            </div>

            <div className="mt-8" data-testid={`crq-question-${step + 1}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-white/50">{QUESTIONS[step].topic}</p>
              <h2 className="mt-2 text-lg font-bold leading-snug sm:text-xl">{QUESTIONS[step].q}</h2>
              <div className="mt-6 space-y-3">
                {QUESTIONS[step].opts.map((opt, i) => {
                  const selected = answers[step] === i;
                  return (
                    <button key={i} type="button"
                      data-testid={`crq-option-${LETTERS[i].toLowerCase()}`}
                      onClick={() => setAnswers((prev) => prev.map((a, j) => (j === step ? i : a)))}
                      className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                        selected
                          ? "border-[#F59E0B] bg-[#F59E0B]/15"
                          : "border-white/15 bg-white/5 hover:border-white/35"
                      }`}>
                      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        selected ? "bg-[#F59E0B] text-[#04302E]" : "bg-white/10 text-white/70"
                      }`}>{LETTERS[i]}</span>
                      <span className="text-sm leading-snug sm:text-base">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button type="button" data-testid="crq-back-btn"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white disabled:invisible">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button type="button" data-testid="crq-next-btn"
                onClick={() => setStep((s) => s + 1)}
                disabled={answers[step] === null}
                className="inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-6 py-2.5 text-sm font-bold text-[#04302E] transition-opacity disabled:cursor-not-allowed disabled:opacity-40">
                {step === QUESTIONS.length - 1 ? "See my results" : "Next"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}

        {atGate && (
          <div className="mt-12" data-testid="crq-email-gate">
            <div className="rounded-2xl border border-white/15 bg-white/5 p-8 text-center">
              <Mail className="mx-auto h-8 w-8 text-[#F59E0B]" />
              <h2 className="mt-4 text-xl font-extrabold sm:text-2xl">Your results are ready</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-white/70">
                Enter your email to see your Capital Readiness Score, your readiness band,
                and your personalised action plan.
              </p>
              <form
                className="mx-auto mt-6 flex max-w-md flex-col gap-3 sm:flex-row"
                onSubmit={(e) => { e.preventDefault(); submitLead(); }}>
                <input type="email" value={email}
                  data-testid="crq-email-input"
                  onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                  placeholder="you@company.com"
                  className="w-full rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm text-white placeholder-white/40 outline-none focus:border-[#F59E0B]" />
                <button type="submit" disabled={submitting}
                  data-testid="crq-see-results-btn"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#F59E0B] px-6 py-3 text-sm font-bold text-[#04302E] transition-opacity hover:opacity-90 disabled:opacity-50">
                  {submitting ? "One moment…" : "See my results"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
              {emailError && (
                <p className="mt-3 text-sm font-semibold text-red-400" data-testid="crq-email-error">
                  {emailError}
                </p>
              )}
              <p className="mt-4 text-[11px] text-white/40">
                We'll only use your email to share capital-readiness resources. No spam.
              </p>
            </div>
          </div>
        )}

        {done && (
          <div className="mt-10" data-testid="crq-results">
            <div className={`rounded-2xl border ${band.border} ${band.bg} p-8 text-center`}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">Your Capital Readiness Score</p>
              <p className="mt-3 text-6xl font-extrabold sm:text-7xl" style={{ color: band.color }}
                data-testid="crq-score">{score}</p>
              <p className="text-sm font-semibold text-white/60">out of 100</p>
              <p className={`mt-3 text-xl font-extrabold ${band.text}`} data-testid="crq-band">{band.label}</p>
            </div>

            <p className="mt-6 text-sm leading-relaxed text-white/80 sm:text-base" data-testid="crq-summary">
              {band.summary}
            </p>

            <div className="mt-10" data-testid="crq-action-plan">
              <h2 className="text-lg font-extrabold sm:text-xl">Your Action Plan</h2>
              {gaps.length === 0 ? (
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-green-500/40 bg-green-500/10 p-4"
                  data-testid="crq-no-gaps">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-400" />
                  <p className="text-sm leading-relaxed text-white/85">
                    No gaps identified. You are well prepared — focus your energy on initiating funder
                    conversations and managing the engagement process.
                  </p>
                </div>
              ) : (
                <ol className="mt-4 space-y-3">
                  {gaps.map((g, i) => (
                    <li key={g.topic} className="flex items-start gap-3 rounded-xl border border-white/15 bg-white/5 p-4"
                      data-testid={`crq-action-item-${i + 1}`}>
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white/70">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-white/50">{g.topic}</p>
                        <p className="mt-1 text-sm leading-relaxed text-white/85">{g.action}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <button type="button" data-testid="crq-retake-btn" onClick={reset}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-bold text-white hover:border-white/50">
                <RotateCcw className="h-4 w-4" /> Retake the Quiz
              </button>
              <a href="https://www.clinq.africa" data-testid="crq-cta-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F59E0B] px-6 py-3 text-sm font-bold text-[#04302E] hover:opacity-90">
                <Rocket className="h-4 w-4" /> Start Building on Clinq
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
